<script setup>
import { ref, onMounted } from 'vue';

const showBanner = ref(false);
const showPreferences = ref(false);
const cookiePreferences = ref({
    necessary: true, // Always true, cannot be disabled
    functional: false,
    analytics: false,
    marketing: false
});

const cookieCategories = {
    necessary: {
        name: 'Nujni piškotki',
        description: 'Ti piškotki so potrebni za osnovno delovanje spletne strani in jih ni mogoče onemogočiti.',
        required: true
    },
    functional: {
        name: 'Funkcionalni piškotki',
        description: 'Ti piškotki omogočajo izboljšano funkcionalnost in personalizacijo.',
        required: false
    },
    analytics: {
        name: 'Analitični piškotki',
        description: 'Ti piškotki nam pomagajo razumeti, kako obiskovalci uporabljajo spletno stran.',
        required: false
    },
    marketing: {
        name: 'Trženjski piškotki',
        description: 'Ti piškotki se uporabljajo za prikazovanje oglasov, ki so relevantni za vas.',
        required: false
    }
};

onMounted(() => {
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
        showBanner.value = true;
    } else {
        // Load saved preferences
        const savedPreferences = localStorage.getItem('cookiePreferences');
        if (savedPreferences) {
            cookiePreferences.value = { ...cookiePreferences.value, ...JSON.parse(savedPreferences) };
        }
    }
    
    // Add global function to reset cookie consent for testing
    window.resetCookieConsent = () => {
        localStorage.removeItem('cookieConsent');
        localStorage.removeItem('cookiePreferences');
        localStorage.removeItem('cookieConsentDate');
        showBanner.value = true;
    };
    
    // Add global function to force show banner
    window.showCookieBanner = () => {
        showBanner.value = true;
    };
    
    // Add global function to check banner state
    window.checkBannerState = () => {
        console.log('Banner state:', showBanner.value);
        console.log('Has consent:', localStorage.getItem('cookieConsent'));
        console.log('Preferences:', localStorage.getItem('cookiePreferences'));
    };
});

const acceptAll = () => {
    cookiePreferences.value = {
        necessary: true,
        functional: true,
        analytics: true,
        marketing: true
    };
    savePreferences();
    showBanner.value = false;
};

const acceptNecessary = () => {
    cookiePreferences.value = {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false
    };
    savePreferences();
    showBanner.value = false;
};

const savePreferences = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences.value));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Trigger custom event for other components to listen
    window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', {
        detail: cookiePreferences.value
    }));
};

const openPreferences = () => {
    showPreferences.value = true;
};

const closePreferences = () => {
    showPreferences.value = false;
};

const saveCustomPreferences = () => {
    savePreferences();
    showPreferences.value = false;
    showBanner.value = false;
};

const toggleCategory = (category) => {
    if (!cookieCategories[category].required) {
        cookiePreferences.value[category] = !cookiePreferences.value[category];
    }
};
</script>

<template>
    <!-- Cookie Banner -->
    <div v-if="showBanner" class="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div class="container mx-auto px-4 py-6">
            <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div class="flex-1">
                    <h3 class="text-lg font-semibold text-gray-900 mb-2">Sporočilo o piškotkih</h3>
                    <p class="text-gray-600 text-sm">
                        Uporabljamo piškotke za izboljšanje vaše izkušnje na naši spletni strani. 
                        Z nadaljnjim brskanjem se strinjate z našo 
                        <a :href="route('web.cookie-policy')" class="text-theme-green-dark hover:underline">politiko piškotkov</a>.
                    </p>
                </div>
                <div class="flex flex-col sm:flex-row gap-2">
                    <button
                        @click="openPreferences"
                        class="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                        Nastavitve
                    </button>
                    <button
                        @click="acceptNecessary"
                        class="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                        Samo nujni
                    </button>
                    <button
                        @click="acceptAll"
                        class="px-4 py-2 text-sm text-white bg-theme-green-dark hover:bg-iron-grey rounded-md transition-colors"
                    >
                        Sprejmi vse
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Cookie Preferences Modal -->
    <div v-if="showPreferences" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-screen items-center justify-center p-4">
            <div class="fixed inset-0 bg-black bg-opacity-50" @click="closePreferences"></div>
            
            <div class="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div class="p-6">
                    <div class="flex justify-between items-center mb-6">
                        <h2 class="text-2xl font-bold text-gray-900">Nastavitve piškotkov</h2>
                        <button
                            @click="closePreferences"
                            class="text-gray-400 hover:text-gray-600"
                        >
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>

                    <p class="text-gray-600 mb-6">
                        Spodaj lahko izberete, katere piškotke želite sprejeti. 
                        Nujni piškotki so potrebni za osnovno delovanje spletne strani.
                    </p>

                    <div class="space-y-4 mb-6">
                        <div v-for="(category, key) in cookieCategories" :key="key" 
                             class="border border-gray-200 rounded-lg p-4">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <div class="flex items-center gap-3">
                                        <h3 class="text-lg font-semibold text-gray-900">{{ category.name }}</h3>
                                        <span v-if="category.required" 
                                              class="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                            Obvezno
                                        </span>
                                    </div>
                                    <p class="text-gray-600 text-sm mt-1">{{ category.description }}</p>
                                </div>
                                <div class="ml-4">
                                    <label class="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            :checked="cookiePreferences[key]"
                                            :disabled="category.required"
                                            @change="toggleCategory(key)"
                                            class="sr-only peer"
                                        >
                                        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-theme-green-light rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme-green-dark"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col sm:flex-row gap-3 justify-end">
                        <button
                            @click="closePreferences"
                            class="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                            Prekliči
                        </button>
                        <button
                            @click="saveCustomPreferences"
                            class="px-4 py-2 text-sm text-white bg-theme-green-dark hover:bg-iron-grey rounded-md transition-colors"
                        >
                            Shrani nastavitve
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
